import { useEffect, useMemo, useState } from 'react';

import { usePipelines } from '../models/pipelines/queries';

/**
 * Pipeline + stage picker state for "create deals in …" dialogs.
 *
 * Preselects `initialPipelineId`/`initialStageId` (if that pipeline still exists), else the default
 * pipeline, once per opening; the selection is cleared when `isOpen` turns false. The stage falls
 * back to the pipeline's first stage whenever the current one doesn't belong to it.
 *
 * @param {Object} options
 * @param {boolean} options.isOpen
 * @param {string|null} [options.initialPipelineId]
 * @param {string|null} [options.initialStageId]
 */
export function usePipelineStageSelection({ isOpen, initialPipelineId = null, initialStageId = null }) {
	const { data: pipelines = [], isLoading } = usePipelines();
	const [pipelineId, setPipelineId] = useState('');
	const [stageId, setStageId] = useState('');

	useEffect(() => {
		if (!isOpen) {
			setPipelineId('');
			setStageId('');
			return;
		}
		if (pipelineId || pipelines.length === 0) return;

		const initial = pipelines.find((p) => p.id === initialPipelineId);
		if (initial) {
			setPipelineId(initial.id);
			setStageId(initialStageId ?? '');
		} else {
			setPipelineId((pipelines.find((p) => p.isDefault) ?? pipelines[0]).id);
		}
	}, [isOpen, pipelines, pipelineId, initialPipelineId, initialStageId]);

	const stages = useMemo(
		() =>
			(pipelines.find((p) => p.id === pipelineId)?.stages ?? [])
				.slice()
				.sort((a, b) => a.stageOrder - b.stageOrder),
		[pipelines, pipelineId],
	);

	useEffect(() => {
		if (stages.length > 0 && !stages.some((s) => s.id === stageId)) {
			setStageId(stages[0].id);
		}
	}, [stages, stageId]);

	return {
		pipelines,
		stages,
		isLoading,
		pipelineId,
		setPipelineId,
		stageId,
		setStageId,
		isReady: Boolean(pipelineId && stageId),
	};
}
